import * as React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  Divider,
  TextField,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  StepContent,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useAuthenticationStore } from "../stores/auth";
import { useEffect, useMemo, useState } from "react";
import { MarkSchemeQuickCreate } from "../features/question/components/LongTextMarkSchemeStep";
import { useApiClient } from "../api";
import { FileDropZone } from "../features/assignment/components/FileDropZone";
import { useAnswerFileUploader } from "../features/assignment/hooks/useAnswerFileUploader";
import {
  extractFromWordDoc,
  extractTextFromPdf,
} from "../features/assignment/components/AnswerFileUploadDialog";
import { enqueueSnackbar } from "notistack";
import GeneralLoading from "./Loading";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { alpha } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import { checkUserHasRole } from "../features/auth/Utils";
import {
  FruitifySchoolClassSearch,
  SchoolClassListItem,
} from "../features/school/components/FruitifySchoolClassSearch";
import { SubjectYearGroupPicker } from "../features/question/components/SubjectYearGroupPicker";
import { IdentifierAutoComplete } from "../features/assignment/components/IdentifierAutoComplete";

const steps = [
  {
    label: "Get started",
    description:
      "Lets get you started with a quick workflow to submit an answer",
  },
  {
    label: "Choose your class",
    description: "Create or select a class to set the assignment for.",
  },
  {
    label: "Add your question",
    description:
      "Add a question - ideally one that you already have an answer for!",
  },
  {
    label: "Submit answer",
    description: "Submit an answer and let Fruitify do the rest!",
  },
];
const SubmissionType = {
  TYPED: "typed",
  HANDWRITTEN_MOBILE: "handwritten-mobile",
  HANDWRITTEN_PC: "handwritten-pc",
};

export const QuickStartDemoDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [question, setQuestion] = useState("");
  const [assignmentName, setAssignmentName] = useState("");
  const [studentName, setStudentName] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [sourceExtract, setSourceExtract] = useState("");
  const [answer, setAnswer] = useState("");
  const [submissionType, setSubmissionType] = useState(SubmissionType.TYPED);
  const [chosenAos, setChosenAos] = useState({});
  const [yearGroup, setYearGroup] = useState(null);
  const [subject, setSubject] = useState(null);
  const [educationLevel, setEducationLevel] = useState(null);
  const [yearGroups, setYearGroups] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [students, setStudents] = useState([]);
  const [assignment, setAssignment] = useState(null);
  const [schoolClass, setSchoolClass] = useState(null);
  const [schoolClassName, setSchoolClassName] = useState("");
  const [hiddenImageIds, setHiddenImageIds] = useState(new Set());
  const uploadToken = useMemo(
    () =>
      globalThis.crypto?.randomUUID
        ? globalThis.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).substring(2)}`,
    [open],
  );
  const [availableAssessmentObjectives, setAvailableAssessmentObjectives] =
    useState([]);
  const ApiClient = useApiClient();
  const { user, updateUserInfo } = useAuthenticationStore();
  const isTeacher = checkUserHasRole({ user: user, role: "teacher" });
  if (!isTeacher) {
    throw Error("User must have an active teacher role");
  }
  const { roleAssignment } = user.info.activeContext;
  const { subjects, educationLevels } = roleAssignment;
  const navigate = useNavigate();

  const fileUploader = useAnswerFileUploader({
    isIndependentUser: false,
    student: {},
  });

  useEffect(() => {
    const subscription = ApiClient.core.pollTemporaryImages({
      uploadToken: uploadToken,
      pollInterval: 5000,
      onUpdate: (data) => setUploadedImages(data),
    });

    return () => subscription.unsubscribe();
  }, [uploadToken]);

  const handleSubmit = async () => {
    setSubmitting(true);
    let tempFilePreview = null;
    if (submissionType === SubmissionType.HANDWRITTEN_PC) {
      tempFilePreview = {
        img: URL.createObjectURL(fileUploader.file),
        type: "pdf",
        name: fileUploader.file.name,
        file: fileUploader.file,
      };
    }
    try {
      const response = await ApiClient.assignment.quickStartDemoSubmit({
        schoolClass: schoolClass?.id,
        schoolClassName: schoolClassName,
        student: identifier?.id,
        studentName: studentName,
        assignmentName: assignmentName,
        questionText: question,
        sourceExtract: sourceExtract,
        chosenObjectives: JSON.stringify(chosenAos),
        answerText: answer,
        yearGroup: yearGroup.id,
        subject: subject.id,
        temporaryImages:
          submissionType === SubmissionType.HANDWRITTEN_MOBILE
            ? visibleImages.map((img) => img.id)
            : [],
        handwrittenImageFile: tempFilePreview,
      });
      if (response && response.success) {
        enqueueSnackbar("Successfully submitted", {
          variant: "success",
          autoHideDuration: 3000,
        });
        setAssignment(response.assignment);
        try {
          // Should trigger a refresh of the dashboard
          const userData = await ApiClient.users.me();
          updateUserInfo(userData);
        } catch (error) {
          console.error("Failed to fetch user details:", error);
        }
      } else {
        console.error("Error submitting quick start demo");
        enqueueSnackbar("There was a problem. Please try again.", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Error submitting quick start demo:", error);
      enqueueSnackbar("There was a problem. Please try again.", {
        variant: "error",
        autoHideDuration: 3000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleSubmit();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setAssignment(null);
    setAnswer("");
    setQuestion("");
    setSourceExtract("");
    fileUploader.setFile(null);
    setYearGroup(null);
    setChosenAos({});
    setSubmissionType(SubmissionType.TYPED);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };
  const visibleImages = useMemo(() => {
    return uploadedImages.filter((img) => !hiddenImageIds.has(img.id));
  }, [uploadedImages, hiddenImageIds]);

  const fetchAssessmentObjectives = async () => {
    try {
      const response = await ApiClient.question.getAssessmentObjectives({
        educationLevel: educationLevel.id,
        subject: subject.id,
      });
      if (response) {
        setAvailableAssessmentObjectives(response.assessmentObjectives);
      }
    } catch (error) {
      console.error("Failed to fetch AOs", error);
    }
  };

  const handleSubmissionTypeChange = (newSubmissionType) => {
    setSubmissionType(newSubmissionType);
    fileUploader.setFile(null);
    setAnswer("");
    setUploadedImages([]);
  };

  useEffect(() => {
    if (subject && educationLevel) {
      fetchAssessmentObjectives();
    }
  }, [subject, educationLevel]);

  const extractFileText = () => {
    const wordExtensions = [".doc", ".docx"];
    const { file } = fileUploader;

    if (file.name.toLowerCase().endsWith(".pdf")) {
      extractTextFromPdf(setAnswer, file);
    } else if (
      wordExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))
    ) {
      extractFromWordDoc(setAnswer, file);
    } else {
      throw Error("Unknown file type");
    }
  };

  const fetchSchoolClass = async (chosenClass) => {
    try {
      const response = await ApiClient.schools.getSchoolClassData({
        schoolClass: chosenClass?.id,
      });
      if (response) {
        setStudents(response.schoolClass.students);
      }
    } catch (error) {
      console.error("Failed to fetch school class", error);
    }
  };

  useEffect(() => {
    if (fileUploader.file && submissionType === SubmissionType.TYPED) {
      extractFileText();
    }
  }, [fileUploader]);

  useEffect(() => {
    if (educationLevel) {
      setYearGroups(educationLevel.yearGroups);
    } else {
      setYearGroups([]);
      setYearGroup(null);
    }
  }, [educationLevel]);

  const checkStepIsComplete = (step) => {
    if (step === 0) return true;
    if (step === 1)
      return (
        schoolClass?.id ||
        (schoolClassName?.trim() && yearGroup?.id && subject?.id)
      );
    if (step === 2)
      return (
        question.trim() !== "" &&
        Object.keys(chosenAos).length > 0 &&
        assignmentName.trim() !== ""
      );
    if (step === 3) {
      const studentSelected = identifier?.id || studentName?.trim() !== "";
      if (submissionType === SubmissionType.TYPED) {
        return answer.trim() !== "" && studentSelected;
      } else if (submissionType === SubmissionType.HANDWRITTEN_MOBILE) {
        return visibleImages.length > 0 && studentSelected;
      } else if (submissionType === SubmissionType.HANDWRITTEN_PC) {
        return fileUploader.file !== null && studentSelected;
      }
    }
    return false;
  };

  const handleSchoolClassSelect = (schoolClass) => {
    setSchoolClass(schoolClass);
    setYearGroup(schoolClass.yearGroup);
    setSubject(schoolClass.subject);
    setEducationLevel(schoolClass.educationLevel);
    fetchSchoolClass(schoolClass);
  };

  const handleRemoveImage = (id) => {
    setHiddenImageIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const renderContent = () => {
    const mockQuestionObject = {
      subject: { id: subjects[0].id },
      yearGroup: { educationLevel: { id: educationLevels[0].id } },
    };
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h4" gutterBottom>
              Welcome to Fruitify 👋
            </Typography>

            <Typography variant="body1" sx={{ mt: 2 }}>
              This is a simplified walkthrough of Fruitify. It’s a shortened
              version of our usual workflow designed to help you quickly see how
              creating questions, submitting answers, and generating feedback
              works in practice.
            </Typography>

            <Typography variant="body1" sx={{ mt: 2 }}>
              It skips some setup and configuration steps, but gives you a
              realistic feel for how the platform works and the results you can
              expect.
            </Typography>

            <Typography variant="body1" sx={{ mt: 2 }}>
              You can re launch this quick start workflow at any time by
              clicking on the quick start button in the bottom right corner of
              the screen.
            </Typography>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h4" gutterBottom>
              Choose your class
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select an existing class or create a new one to continue.
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Choose an existing class
              </Typography>
              {schoolClass && (
                <SchoolClassListItem
                  deselect
                  schoolClass={schoolClass}
                  onSelect={() => {
                    setSchoolClass(null);
                    setStudents([]);
                  }}
                />
              )}

              <FruitifySchoolClassSearch
                listView
                showToggle={false}
                onSelect={handleSchoolClassSelect}
                selected={[schoolClass]}
              />
            </Box>
            {!schoolClass && (
              <>
                <Divider sx={{ my: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    OR
                  </Typography>
                </Divider>

                <Box>
                  <Typography variant="h6" gutterBottom>
                    Create a new class
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Give your class a name so you can add students and
                    assignments.
                  </Typography>

                  <TextField
                    fullWidth
                    label="Class Name"
                    value={schoolClassName}
                    onChange={(e) => setSchoolClassName(e.target.value)}
                  />
                  <SubjectYearGroupPicker
                    subjects={subjects}
                    educationLevels={educationLevels}
                    yearGroups={yearGroups}
                    selectedSubject={subject}
                    setSelectedSubject={setSubject}
                    selectedEducationLevel={educationLevel}
                    setSelectedEducationLevel={setEducationLevel}
                    selectedYearGroup={yearGroup}
                    setSelectedYearGroup={setYearGroup}
                    stackProps={{
                      direction: "row",
                      width: "100%",
                      spacing: 2,
                      mt: 2,
                      mb: 2,
                    }}
                  />
                </Box>
              </>
            )}
          </Box>
        );
      case 2:
        return (
          <Box>
            <Typography variant="h4" gutterBottom>
              Create your question and mark scheme
            </Typography>

            <Typography variant="body1" sx={{ mb: 2 }}>
              Add a question - ideally one that you already have an answer for!
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              This should be for {subjects[0].name} {educationLevels[0].level}{" "}
              students based on the subject and education level you selected.
            </Typography>
            <Typography variant="body1" gutterBottom>
              <strong>Note: </strong>Make sure the mark scheme objectives are
              added correctly to ensure accurate marking
            </Typography>
            <Stack direction={"column"} spacing={2} sx={{ mt: 2 }}>
              <Typography variant="h5" sx={{ mb: 2 }}>
                Assignment details
              </Typography>
              <TextField
                fullWidth
                label="Assignment Name"
                value={assignmentName}
                onChange={(e) => setAssignmentName(e.target.value)}
              />
            </Stack>
            <Stack direction={"column"} spacing={2} sx={{ mt: 2 }}>
              <Typography variant="h5" sx={{ mb: 2 }}>
                Question details
              </Typography>
              <TextField
                fullWidth
                rows={4}
                label="Question"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
              />
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Source Extract (Optional)"
                value={sourceExtract}
                onChange={(e) => setSourceExtract(e.target.value)}
              />
            </Stack>
            <Stack direction={"column"} spacing={2} sx={{ mt: 4 }}>
              <Divider />
              <MarkSchemeQuickCreate
                question={mockQuestionObject}
                sortedAvailableObjectives={availableAssessmentObjectives}
                collapsible={false}
                title={"Mark scheme details"}
                onAOChange={setChosenAos}
                hideSmartSuggest
              />
            </Stack>
          </Box>
        );

      case 3:
        return assignment ? (
          <Box>
            <Typography variant="h4" gutterBottom>
              Your assignment is ready!
            </Typography>

            <Typography variant="body1" sx={{ mb: 2 }}>
              Click the button below to view the assignment page. You will see
              the status of your submitted answer in the submission box at the
              bottom of the page. Once marked (this shouldn't take more than 30
              seconds), you can view the feedback.
            </Typography>

            <Button
              variant="contained"
              onClick={() => {
                navigate(`/assignments/view/${assignment.id}`);
                handleClose();
              }}
            >
              View Results
            </Button>
          </Box>
        ) : (
          <Box>
            <Typography variant="h4" gutterBottom>
              Submit a students answer
            </Typography>
            <>
              <Typography variant="h5" gutterBottom>
                Choose or add a new student
              </Typography>

              <Stack spacing={2} sx={{ mb: 2, mt: 2 }}>
                <IdentifierAutoComplete
                  availableIdentifiers={students}
                  setIdentifier={setIdentifier}
                  identifier={identifier}
                />
                {!identifier && (
                  <>
                    <Divider>
                      <Typography variant="body2" color="text.secondary">
                        OR
                      </Typography>
                    </Divider>

                    <TextField
                      fullWidth
                      label="Student Name"
                      value={studentName}
                      onChange={(e) => setStudentName(e.target.value)}
                    />
                  </>
                )}
              </Stack>

              <Typography variant="h5" gutterBottom>
                Submit an answer
              </Typography>
              <Typography variant="body1" gutterBottom>
                There are several ways to submit an answer:
              </Typography>

              <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                <li>
                  <Typography variant="body1">
                    <strong>Typed</strong> — Submit a typed answer by
                    copy/pasting into the box or uploading a PDF / Word document
                    with a typed response.
                  </Typography>
                </li>

                <li>
                  <Typography variant="body1">
                    <strong>Handwritten (mobile)</strong> — Use a QR code to
                    take a picture of your answer on your phone and upload it
                    instantly.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    <strong>Handwritten (PC)</strong> — Upload a PDF / Word
                    document containing a handwritten response.
                  </Typography>
                </li>
              </Box>
              <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                <strong>Note: </strong>If uploading a handwritten image, ensure
                its portrait and the image is in focus.
              </Typography>
            </>
            <ToggleButtonGroup
              fullWidth
              value={submissionType}
              exclusive
              onChange={(_, newSubmissionType) =>
                handleSubmissionTypeChange(newSubmissionType)
              }
              aria-label="Submission Type"
            >
              <ToggleButton value={SubmissionType.TYPED}>Typed</ToggleButton>
              <ToggleButton value={SubmissionType.HANDWRITTEN_MOBILE}>
                Handwritten (mobile)
              </ToggleButton>
              <ToggleButton value={SubmissionType.HANDWRITTEN_PC}>
                Handwritten (PC)
              </ToggleButton>
            </ToggleButtonGroup>
            {submissionType === SubmissionType.TYPED && (
              <>
                <FormControl fullWidth>
                  <Box
                    sx={{
                      position: "relative",
                      display: "inline-block",
                      width: "100%",
                    }}
                  >
                    <TextField
                      multiline
                      minRows={6}
                      maxRows={16}
                      fullWidth
                      margin="normal"
                      value={answer}
                      onChange={(e) => setAnswer(e.target.value)}
                      placeholder="Type / paste the answer here or upload the processed document below..."
                    />
                  </Box>
                </FormControl>

                <Divider orientation="horizontal" flexItem sx={{ mx: 1 }}>
                  OR
                </Divider>
                <FormControl fullWidth>
                  <FileDropZone
                    files={fileUploader.file ? [fileUploader.file] : []}
                    onFilesChange={fileUploader.handleFileUpload}
                  />
                </FormControl>
              </>
            )}

            {submissionType === SubmissionType.HANDWRITTEN_MOBILE && (
              <>
                <Stack
                  direction="column"
                  spacing={2}
                  alignItems="center"
                  sx={{ mt: 2 }}
                >
                  <Typography gutterBottom textAlign="center">
                    Scan this QR code with your phone to submit a handwritten
                    answer
                  </Typography>

                  <Box my={1}>
                    <QRCodeSVG
                      value={`${window.location.origin}/demo/temp-image-upload?token=${uploadToken}`}
                      size={150}
                    />
                  </Box>
                </Stack>
                <Typography variant="h5" mb={1}>
                  Uploaded images:
                </Typography>
                {visibleImages.length === 0 ? (
                  <GeneralLoading
                    direction={"row"}
                    message={"Mobile uploaded images will appear here..."}
                    size={30}
                  />
                ) : (
                  <Box>
                    <Stack direction="row" spacing={1} overflow="auto">
                      {visibleImages.map((file, index) => (
                        <Box
                          key={index}
                          position="relative"
                          width={100}
                          height={100}
                          borderRadius={2}
                          overflow="hidden"
                          border="1px solid"
                          sx={{ flexShrink: 0, borderColor: "divider" }}
                        >
                          <img
                            src={file.preSignedUrl}
                            alt={`preview-${index}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveImage(file.id)}
                            sx={{
                              position: "absolute",
                              top: 2,
                              right: 2,
                              backgroundColor: (theme) =>
                                alpha(theme.palette.common.black, 0.5),
                              color: "common.white",
                              "&:hover": {
                                backgroundColor: (theme) =>
                                  alpha(theme.palette.common.black, 0.7),
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </>
            )}

            {submissionType === SubmissionType.HANDWRITTEN_PC && (
              <FileDropZone
                files={fileUploader.file ? [fileUploader.file] : []}
                onFilesChange={fileUploader.handleFileUpload}
              />
            )}
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          height: "90vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle>Quick Start</DialogTitle>

      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            minHeight: { xs: "auto", md: 450 },
          }}
        >
          {/* LEFT: Stepper (25%) */}
          <Box sx={{ width: { xs: "100%", md: "25%" }, pr: { md: 2 } }}>
            <Stepper
              activeStep={activeStep}
              orientation="vertical"
              connector={null}
            >
              {steps.map((step) => (
                <Step key={step.label}>
                  <StepLabel>{step.label}</StepLabel>
                  <StepContent>
                    <Typography>{step.description}</Typography>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </Box>

          {/* Divider */}
          <Divider
            orientation={isMobile ? "horizontal" : "vertical"}
            flexItem
          />

          {/* RIGHT: Content (75%) */}
          {submitting ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                width: "100%",
                height: "100%",
                minHeight: "70vh",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <GeneralLoading message={"Getting your assignment ready..."} />
            </Box>
          ) : (
            <Box sx={{ flex: 1, pl: { xs: 0, md: 3 }, pt: { xs: 2, md: 0 } }}>
              {renderContent()}

              {(!assignment || activeStep < steps.length - 1) && (
                <Box sx={{ mt: 4, display: "flex", gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={!checkStepIsComplete(activeStep)}
                  >
                    {activeStep === steps.length - 1
                      ? "Submit"
                      : activeStep === 0
                        ? "Start"
                        : "Continue"}
                  </Button>
                  {activeStep !== 0 && (
                    <Button onClick={handleBack}>Back</Button>
                  )}

                  {activeStep === steps.length - 1 && (
                    <Button onClick={handleReset}>Restart</Button>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
